import { usePlanType } from "@tm/ui";
import { theme } from "@tm/utility/global";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trackListingClick, trackListingView } from "../../trackingUtils";
import {
  BackButton,
  CompareHeader,
  CompareSubtitle,
  CompareTitle,
  CompareWrapper,
  ContentCard,
  ContentCardInner,
  FloatingBadgeTag,
  HeaderLeft,
  HeaderRight,
  Table,
  TableScrollArea,
} from "./ComparePlans.styles";
import CompareTableFooter from "./CompareTableFooter";
import CompareTableHeader from "./CompareTableHeader";
import CompareTableRow from "./CompareTableRow";
import FilterTabs from "./FilterTabs";
import {
  IComparePlansBenefit,
  IComparePlansFooterCta,
  IComparePlansProps,
} from "./types";

const BackArrowSvg = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M19 12H5M5 12l6-6M5 12l6 6"
      stroke={theme.blackText}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BOTTOM_MARGIN = 24;

const ComparePlans = ({ data, onClose, onBuyNow }: IComparePlansProps) => {
  const { title, subtitle, filters, header, footer, body } = data;
  const { type } = usePlanType();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const safeHeader = useMemo(() => header || {}, [header]);
  const safeBody = useMemo(
    () => (body || []).filter(Boolean) as IComparePlansBenefit[],
    [body]
  );
  const safeFooter = useMemo(
    () => (footer?.data ? footer : { label: "", data: {} }),
    [footer]
  );

  const [activeFilter, setActiveFilter] = useState("");

  useEffect(() => {
    const updateHeight = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      el.style.maxHeight = `${window.innerHeight - top - BOTTOM_MARGIN}px`;
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    trackListingView("comparenow_opened", type);
  }, [type]);

  const planIds = useMemo(() => Object.keys(safeHeader), [safeHeader]);

  const recommendedId = useMemo(() => {
    for (const id of planIds) {
      if (safeHeader[id]?.badge) return id;
    }
    return null;
  }, [safeHeader, planIds]);
  const recommendedIndex = useMemo(
    () => (recommendedId ? planIds.indexOf(recommendedId) : -1),
    [planIds, recommendedId]
  );
  const recommendedBadge =
    recommendedId && recommendedIndex >= 0
      ? safeHeader[recommendedId]?.badge
      : null;

  const handleClose = useCallback(() => {
    trackListingClick("compare_closed", type);
    onClose?.();
  }, [onClose, type]);

  const handleFilterChange = useCallback(
    (value: string) => {
      const newFilter = activeFilter === value ? "" : value;
      setActiveFilter(newFilter);
      trackListingClick(
        newFilter ? `compare_filter_${newFilter}` : "compare_filter_cleared",
        type
      );
    },
    [activeFilter, type]
  );

  const filteredBenefits = useMemo(() => {
    if (!activeFilter || !filters?.length) return safeBody;
    return safeBody.filter(
      (b: IComparePlansBenefit) =>
        b.filterType === activeFilter || b.type === activeFilter
    );
  }, [safeBody, activeFilter, filters]);

  const handleBuyPress = useCallback(
    (params: IComparePlansFooterCta["params"]) => {
      try {
        onBuyNow?.(params);
      } catch (error) {
        console.error("Compare buy action failed", error);
      }
    },
    [onBuyNow]
  );

  if (!planIds.length) return null;

  return (
    <CompareWrapper ref={wrapperRef}>
      <CompareHeader>
        <HeaderLeft>
          <BackButton onClick={handleClose} aria-label="Back to plans">
            <BackArrowSvg />
          </BackButton>
          <div>
            <CompareTitle>{title}</CompareTitle>
            {subtitle && <CompareSubtitle>{subtitle}</CompareSubtitle>}
          </div>
        </HeaderLeft>
        <HeaderRight>
          {filters && filters.length > 0 && (
            <FilterTabs
              filters={filters}
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
            />
          )}
        </HeaderRight>
      </CompareHeader>

      <ContentCard>
        {recommendedBadge?.text && planIds.length > 0 && recommendedIndex >= 0 && (
          <FloatingBadgeTag
            style={{
              left: `calc(214px + ((100% - 214px) / ${planIds.length}) * ${
                recommendedIndex + 0.5
              })`,
            }}
            $gradient={recommendedBadge.styles?.bgColorGradient}
            $color={recommendedBadge.styles?.color}
          >
            {recommendedBadge.text}
          </FloatingBadgeTag>
        )}
        <ContentCardInner>
          <Table $planCount={planIds.length}>
            <CompareTableHeader
              header={safeHeader}
              planIds={planIds}
              recommendedId={recommendedId}
            />
          </Table>
          <TableScrollArea>
            <Table $planCount={planIds.length}>
              {filteredBenefits.map((benefit, index) => (
                <CompareTableRow
                  key={`${benefit?.label?.text || "benefit"}-${index}`}
                  benefit={benefit}
                  planIds={planIds}
                  recommendedId={recommendedId}
                  rowIndex={index}
                />
              ))}
            </Table>
          </TableScrollArea>
          <Table $planCount={planIds.length}>
            <CompareTableFooter
              footer={safeFooter}
              planIds={planIds}
              recommendedId={recommendedId}
              onBuyPress={handleBuyPress}
            />
          </Table>
        </ContentCardInner>
      </ContentCard>
    </CompareWrapper>
  );
};

export default ComparePlans;
